import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Bot, Power, MessageSquare, Clock, Send, Settings, Plus, Edit2, Trash2, Save, X, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to extract error message from API responses
function getErrorMessage(error, fallback = 'Something went wrong') {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
  return JSON.stringify(detail);
}

export default function Chatbot() {
  const [activeTab, setActiveTab] = useState('setup');
  const [chatbot, setChatbot] = useState(null);
  const [rules, setRules] = useState([]);
  const [flows, setFlows] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form states
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showFlowModal, setShowFlowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [editingFlow, setEditingFlow] = useState(null);

  useEffect(() => {
    fetchChatbot();
  }, []);

  useEffect(() => {
    if (activeTab === 'rules') fetchRules();
    if (activeTab === 'flows') fetchFlows();
    if (activeTab === 'history') fetchHistory();
  }, [activeTab]);

  const fetchChatbot = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chatbot`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatbot(response.data);
    } catch (error) {
      console.error('Error fetching chatbot:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chatbot/rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Error fetching rules:', error);
    }
  };

  const fetchFlows = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chatbot/flows`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlows(response.data.flows || []);
    } catch (error) {
      console.error('Error fetching flows:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chatbot/history?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const updateChatbot = async (updates) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/chatbot`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatbot(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Error updating chatbot:', error);
      alert(getErrorMessage(error, 'Failed to update'));
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = () => {
    if (!chatbot.session_id && !chatbot.enabled) {
      alert('Please select a WhatsApp session first');
      return;
    }
    updateChatbot({ enabled: !chatbot.enabled });
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <div style={{ textAlign: 'center', color: '#64748b' }}>Loading chatbot...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Bot size={32} />
            WhatsApp Chatbot
          </h1>
          <p style={{ color: '#64748b', fontSize: '15px' }}>
            Auto-reply to incoming messages with rules and flows
          </p>
        </div>
        
        {/* Enable/Disable Toggle */}
        <button
          onClick={toggleEnabled}
          disabled={saving}
          style={{
            padding: '12px 24px',
            background: chatbot?.enabled ? '#dc2626' : '#16a34a',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '600',
            fontSize: '15px'
          }}
        >
          <Power size={20} />
          {chatbot?.enabled ? 'Disable Chatbot' : 'Enable Chatbot'}
        </button>
      </div>

      {/* Status Card */}
      <div style={{
        background: chatbot?.enabled ? '#dcfce7' : '#fef3c7',
        border: `1px solid ${chatbot?.enabled ? '#86efac' : '#fcd34d'}`,
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          background: chatbot?.enabled ? '#16a34a' : '#f59e0b',
          animation: chatbot?.enabled ? 'pulse 2s infinite' : 'none'
        }} />
        <span style={{ color: chatbot?.enabled ? '#166534' : '#92400e', fontWeight: '500' }}>
          {chatbot?.enabled 
            ? `Chatbot active on session: ${chatbot?.session_id || 'Not selected'}` 
            : 'Chatbot is disabled'}
        </span>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        marginBottom: '24px',
        borderBottom: '1px solid #e2e8f0',
        paddingBottom: '12px'
      }}>
        {[
          { id: 'setup', label: 'Setup', icon: Settings },
          { id: 'rules', label: 'Keyword Rules', icon: MessageSquare },
          { id: 'flows', label: 'Flows', icon: ChevronRight },
          { id: 'history', label: 'Chat History', icon: Clock }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              background: activeTab === tab.id ? '#667eea' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#64748b',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '500',
              fontSize: '14px'
            }}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'setup' && (
        <SetupTab chatbot={chatbot} updateChatbot={updateChatbot} saving={saving} />
      )}
      
      {activeTab === 'rules' && (
        <RulesTab 
          rules={rules} 
          fetchRules={fetchRules}
          showModal={showRuleModal}
          setShowModal={setShowRuleModal}
          editingRule={editingRule}
          setEditingRule={setEditingRule}
        />
      )}
      
      {activeTab === 'flows' && (
        <FlowsTab 
          flows={flows}
          fetchFlows={fetchFlows}
          showModal={showFlowModal}
          setShowModal={setShowFlowModal}
          editingFlow={editingFlow}
          setEditingFlow={setEditingFlow}
        />
      )}
      
      {activeTab === 'history' && (
        <HistoryTab history={history} fetchHistory={fetchHistory} />
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </Layout>
  );
}

// Setup Tab Component
function SetupTab({ chatbot, updateChatbot, saving }) {
  const [localState, setLocalState] = useState({
    session_id: chatbot?.session_id || '',
    welcome_enabled: chatbot?.welcome_enabled || false,
    welcome_message: chatbot?.welcome_message || '',
    away_enabled: chatbot?.away_enabled || false,
    away_message: chatbot?.away_message || '',
    business_hours_enabled: chatbot?.business_hours_enabled || false,
    business_hours: chatbot?.business_hours || { start: '09:00', end: '21:00', days: [1,2,3,4,5,6], timezone: 'Asia/Kolkata' },
    default_reply: chatbot?.default_reply || ''
  });

  const handleSave = () => {
    updateChatbot(localState);
  };

  return (
    <div style={{ display: 'grid', gap: '24px', maxWidth: '800px' }}>
      {/* Session Selection */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>
          WhatsApp Session
        </h3>
        <select
          value={localState.session_id}
          onChange={(e) => setLocalState(prev => ({ ...prev, session_id: e.target.value }))}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            fontSize: '14px'
          }}
        >
          <option value="">Select a connected session...</option>
          {(chatbot?.available_sessions || []).map(session => (
            <option key={session.session_id} value={session.session_id}>
              {session.phone_number ? `+${session.phone_number}` : session.session_id} ({session.status})
            </option>
          ))}
        </select>
        <p style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
          Chatbot will only respond to messages on this session
        </p>
      </div>

      {/* Welcome Message */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>Welcome Message</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={localState.welcome_enabled}
              onChange={(e) => setLocalState(prev => ({ ...prev, welcome_enabled: e.target.checked }))}
              style={{ width: '18px', height: '18px' }}
            />
            <span style={{ fontSize: '14px', color: '#64748b' }}>Enabled</span>
          </label>
        </div>
        <textarea
          value={localState.welcome_message}
          onChange={(e) => setLocalState(prev => ({ ...prev, welcome_message: e.target.value }))}
          placeholder="Hello! Welcome to our support. How can I help you today?"
          disabled={!localState.welcome_enabled}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            fontSize: '14px',
            minHeight: '80px',
            resize: 'vertical',
            opacity: localState.welcome_enabled ? 1 : 0.5
          }}
        />
        <p style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
          Sent to first-time contacts only
        </p>
      </div>

      {/* Business Hours */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>Business Hours</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={localState.business_hours_enabled}
              onChange={(e) => setLocalState(prev => ({ ...prev, business_hours_enabled: e.target.checked }))}
              style={{ width: '18px', height: '18px' }}
            />
            <span style={{ fontSize: '14px', color: '#64748b' }}>Enabled</span>
          </label>
        </div>
        <div style={{ display: 'flex', gap: '16px', opacity: localState.business_hours_enabled ? 1 : 0.5 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Start Time</label>
            <input
              type="time"
              value={localState.business_hours.start}
              onChange={(e) => setLocalState(prev => ({ 
                ...prev, 
                business_hours: { ...prev.business_hours, start: e.target.value }
              }))}
              disabled={!localState.business_hours_enabled}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>End Time</label>
            <input
              type="time"
              value={localState.business_hours.end}
              onChange={(e) => setLocalState(prev => ({ 
                ...prev, 
                business_hours: { ...prev.business_hours, end: e.target.value }
              }))}
              disabled={!localState.business_hours_enabled}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
            />
          </div>
        </div>
      </div>

      {/* Away Message */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>Away Message</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={localState.away_enabled}
              onChange={(e) => setLocalState(prev => ({ ...prev, away_enabled: e.target.checked }))}
              style={{ width: '18px', height: '18px' }}
            />
            <span style={{ fontSize: '14px', color: '#64748b' }}>Enabled</span>
          </label>
        </div>
        <textarea
          value={localState.away_message}
          onChange={(e) => setLocalState(prev => ({ ...prev, away_message: e.target.value }))}
          placeholder="We're currently away. Our business hours are Mon-Sat, 9 AM - 9 PM IST."
          disabled={!localState.away_enabled}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            fontSize: '14px',
            minHeight: '80px',
            resize: 'vertical',
            opacity: localState.away_enabled ? 1 : 0.5
          }}
        />
        <p style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
          Sent when messages arrive outside business hours
        </p>
      </div>

      {/* Default Reply */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#1e293b' }}>
          Default Reply
        </h3>
        <textarea
          value={localState.default_reply}
          onChange={(e) => setLocalState(prev => ({ ...prev, default_reply: e.target.value }))}
          placeholder="Sorry, I didn't understand your message. Please try again or contact support."
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            fontSize: '14px',
            minHeight: '80px',
            resize: 'vertical'
          }}
        />
        <p style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
          Sent when no keyword or flow matches the incoming message
        </p>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          padding: '14px 28px',
          background: '#667eea',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: saving ? 'not-allowed' : 'pointer',
          fontWeight: '600',
          fontSize: '15px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          opacity: saving ? 0.7 : 1
        }}
      >
        <Save size={20} />
        {saving ? 'Saving...' : 'Save Settings'}
      </button>
    </div>
  );
}

// Rules Tab Component
function RulesTab({ rules, fetchRules, showModal, setShowModal, editingRule, setEditingRule }) {
  const [formData, setFormData] = useState({
    name: '',
    trigger_type: 'contains',
    keywords: '',
    response_text: '',
    priority: 0,
    enabled: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingRule) {
      setFormData({
        name: editingRule.name,
        trigger_type: editingRule.trigger_type,
        keywords: editingRule.keywords.join(', '),
        response_text: editingRule.response_text,
        priority: editingRule.priority,
        enabled: editingRule.enabled
      });
      setShowModal(true);
    }
  }, [editingRule, setShowModal]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.keywords || !formData.response_text) {
      alert('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k)
      };

      if (editingRule) {
        await axios.put(`${API}/chatbot/rules/${editingRule.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/chatbot/rules`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowModal(false);
      setEditingRule(null);
      setFormData({ name: '', trigger_type: 'contains', keywords: '', response_text: '', priority: 0, enabled: true });
      fetchRules();
    } catch (error) {
      alert(getErrorMessage(error, 'Failed to save rule'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this rule?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/chatbot/rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRules();
    } catch (error) {
      alert(getErrorMessage(error, 'Failed to delete'));
    }
  };

  return (
    <div>
      {/* Add Rule Button */}
      <button
        onClick={() => {
          setEditingRule(null);
          setFormData({ name: '', trigger_type: 'contains', keywords: '', response_text: '', priority: 0, enabled: true });
          setShowModal(true);
        }}
        style={{
          padding: '12px 20px',
          background: '#667eea',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: '500',
          marginBottom: '20px'
        }}
      >
        <Plus size={20} />
        Add Keyword Rule
      </button>

      {/* Rules List */}
      <div style={{ display: 'grid', gap: '12px' }}>
        {rules.length === 0 ? (
          <div style={{ 
            background: '#f8fafc', 
            padding: '40px', 
            borderRadius: '12px', 
            textAlign: 'center',
            color: '#64748b'
          }}>
            No keyword rules yet. Add your first rule to get started.
          </div>
        ) : (
          rules.map(rule => (
            <div
              key={rule.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                opacity: rule.enabled ? 1 : 0.6
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: 0 }}>{rule.name}</h4>
                  <span style={{
                    padding: '2px 8px',
                    background: rule.enabled ? '#dcfce7' : '#fee2e2',
                    color: rule.enabled ? '#166534' : '#991b1b',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500'
                  }}>
                    {rule.enabled ? 'Active' : 'Disabled'}
                  </span>
                  <span style={{
                    padding: '2px 8px',
                    background: '#e0e7ff',
                    color: '#4338ca',
                    borderRadius: '4px',
                    fontSize: '11px'
                  }}>
                    {rule.trigger_type}
                  </span>
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
                  <strong>Keywords:</strong> {rule.keywords.join(', ')}
                </div>
                <div style={{ fontSize: '13px', color: '#475569' }}>
                  <strong>Response:</strong> {rule.response_text.substring(0, 100)}{rule.response_text.length > 100 ? '...' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setEditingRule(rule)}
                  style={{ padding: '8px', background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  <Edit2 size={16} color="#64748b" />
                </button>
                <button
                  onClick={() => handleDelete(rule.id)}
                  style={{ padding: '8px', background: '#fee2e2', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  <Trash2 size={16} color="#dc2626" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Rule Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '500px',
            maxWidth: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                {editingRule ? 'Edit Rule' : 'Add Keyword Rule'}
              </h3>
              <button
                onClick={() => { setShowModal(false); setEditingRule(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={24} color="#64748b" />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '6px' }}>
                  Rule Name *
                </label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Greeting Response"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '6px' }}>
                  Trigger Type
                </label>
                <select
                  value={formData.trigger_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, trigger_type: e.target.value }))}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                >
                  <option value="contains">Contains (message contains keyword)</option>
                  <option value="exact">Exact Match (message equals keyword)</option>
                  <option value="startswith">Starts With (message starts with keyword)</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '6px' }}>
                  Keywords * (comma separated)
                </label>
                <input
                  value={formData.keywords}
                  onChange={(e) => setFormData(prev => ({ ...prev, keywords: e.target.value }))}
                  placeholder="hi, hello, hey, namaste"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '6px' }}>
                  Response Text *
                </label>
                <textarea
                  value={formData.response_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, response_text: e.target.value }))}
                  placeholder="Hello! How can I help you today?"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', minHeight: '100px' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '6px' }}>
                    Priority (higher = checked first)
                  </label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.enabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ fontSize: '14px' }}>Enabled</span>
                  </label>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={saving}
                style={{
                  padding: '12px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginTop: '8px'
                }}
              >
                {saving ? 'Saving...' : (editingRule ? 'Update Rule' : 'Create Rule')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Flows Tab Component
function FlowsTab({ flows, fetchFlows, showModal, setShowModal, editingFlow, setEditingFlow }) {
  const [formData, setFormData] = useState({
    name: '',
    trigger_keyword: '',
    description: '',
    steps: [{ id: 'step_1', message: '', options: [] }],
    enabled: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingFlow) {
      const steps = (editingFlow.steps || [{ id: 'step_1', message: '', options: [] }]).map(step => ({
        ...step,
        options: (step.options || []).map(opt => ({
          trigger: opt.trigger || '',
          response: opt.response || '',
          action: opt.action || (opt.next_step ? 'next_step' : 'end'),
          next_step: opt.next_step || ''
        }))
      }));
      setFormData({
        name: editingFlow.name,
        trigger_keyword: editingFlow.trigger_keyword,
        description: editingFlow.description || '',
        steps,
        enabled: editingFlow.enabled
      });
      setShowModal(true);
    }
  }, [editingFlow, setShowModal]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.trigger_keyword || !formData.steps[0]?.message) {
      alert('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingFlow) {
        await axios.put(`${API}/chatbot/flows/${editingFlow.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/chatbot/flows`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setShowModal(false);
      setEditingFlow(null);
      setFormData({ name: '', trigger_keyword: '', description: '', steps: [{ id: 'step_1', message: '', options: [] }], enabled: true });
      fetchFlows();
    } catch (error) {
      alert(getErrorMessage(error, 'Failed to save flow'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (flowId) => {
    if (!window.confirm('Delete this flow?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/chatbot/flows/${flowId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchFlows();
    } catch (error) {
      alert(getErrorMessage(error, 'Failed to delete'));
    }
  };

  const addStep = () => {
    const newStepId = `step_${formData.steps.length + 1}`;
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, { id: newStepId, message: '', options: [] }]
    }));
  };

  const updateStep = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => i === index ? { ...step, [field]: value } : step)
    }));
  };

  const addOption = (stepIndex) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => i === stepIndex ? {
        ...step,
        options: [...step.options, { trigger: '', response: '', action: 'end', next_step: '' }]
      } : step)
    }));
  };

  const updateOption = (stepIndex, optionIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => i === stepIndex ? {
        ...step,
        options: step.options.map((opt, oi) => oi === optionIndex ? { ...opt, [field]: value } : opt)
      } : step)
    }));
  };

  const removeOption = (stepIndex, optionIndex) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => i === stepIndex ? {
        ...step,
        options: step.options.filter((_, oi) => oi !== optionIndex)
      } : step)
    }));
  };

  const removeStep = (stepIndex) => {
    if (formData.steps.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== stepIndex)
    }));
  };

  return (
    <div>
      <button
        onClick={() => {
          setEditingFlow(null);
          setFormData({ name: '', trigger_keyword: '', description: '', steps: [{ id: 'step_1', message: '', options: [] }], enabled: true });
          setShowModal(true);
        }}
        style={{
          padding: '12px 20px',
          background: '#667eea',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: '500',
          marginBottom: '20px'
        }}
      >
        <Plus size={20} />
        Add Flow
      </button>

      <div style={{ display: 'grid', gap: '12px' }}>
        {flows.length === 0 ? (
          <div style={{ 
            background: '#f8fafc', 
            padding: '40px', 
            borderRadius: '12px', 
            textAlign: 'center',
            color: '#64748b'
          }}>
            No flows yet. Create a flow for menu-based conversations.
          </div>
        ) : (
          flows.map(flow => (
            <div
              key={flow.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #e2e8f0',
                opacity: flow.enabled ? 1 : 0.6
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: 0 }}>{flow.name}</h4>
                    <span style={{
                      padding: '2px 8px',
                      background: flow.enabled ? '#dcfce7' : '#fee2e2',
                      color: flow.enabled ? '#166534' : '#991b1b',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '500'
                    }}>
                      {flow.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    <strong>Trigger:</strong> "{flow.trigger_keyword}" | <strong>Steps:</strong> {flow.steps?.length || 0}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => setEditingFlow(flow)}
                    style={{ padding: '8px', background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                  >
                    <Edit2 size={16} color="#64748b" />
                  </button>
                  <button
                    onClick={() => handleDelete(flow.id)}
                    style={{ padding: '8px', background: '#fee2e2', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                  >
                    <Trash2 size={16} color="#dc2626" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Flow Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '700px',
            maxWidth: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                {editingFlow ? 'Edit Flow' : 'Create Flow'}
              </h3>
              <button
                onClick={() => { setShowModal(false); setEditingFlow(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={24} color="#64748b" />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '6px' }}>
                    Flow Name *
                  </label>
                  <input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Main Menu"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '6px' }}>
                    Trigger Keyword *
                  </label>
                  <input
                    value={formData.trigger_keyword}
                    onChange={(e) => setFormData(prev => ({ ...prev, trigger_keyword: e.target.value }))}
                    placeholder="menu"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                </div>
              </div>

              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Steps</h4>
                
                {formData.steps.map((step, stepIndex) => (
                  <div key={step.id} style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', marginBottom: '12px', position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#6366f1' }}>
                        Step {stepIndex + 1} <span style={{ color: '#94a3b8', fontWeight: '400', fontSize: '11px' }}>({step.id})</span>
                      </div>
                      {formData.steps.length > 1 && (
                        <button
                          onClick={() => removeStep(stepIndex)}
                          style={{ padding: '4px 8px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}
                        >
                          Remove Step
                        </button>
                      )}
                    </div>
                    <textarea
                      value={step.message}
                      onChange={(e) => updateStep(stepIndex, 'message', e.target.value)}
                      placeholder="Reply with:\n1. Products\n2. Support\n3. Pricing"
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', minHeight: '80px', marginBottom: '12px' }}
                    />
                    
                    <div style={{ fontSize: '12px', fontWeight: '500', color: '#64748b', marginBottom: '8px' }}>
                      Options (what user can reply):
                    </div>
                    {step.options.map((option, optIndex) => (
                      <div key={optIndex} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                          <input
                            value={option.trigger}
                            onChange={(e) => updateOption(stepIndex, optIndex, 'trigger', e.target.value)}
                            placeholder="1"
                            style={{ width: '50px', padding: '7px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', textAlign: 'center', fontWeight: '600' }}
                          />
                          <select
                            value={
                              option.action === 'end' ? 'end' 
                              : option.action === 'reply_and_goto' ? `reply_goto:${option.next_step || ''}`
                              : `goto:${option.next_step || ''}`
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              let action, next_step;
                              if (val === 'end') {
                                action = 'end';
                                next_step = '';
                              } else if (val.startsWith('reply_goto:')) {
                                action = 'reply_and_goto';
                                next_step = val.replace('reply_goto:', '');
                              } else {
                                action = 'next_step';
                                next_step = val.replace('goto:', '');
                              }
                              setFormData(prev => ({
                                ...prev,
                                steps: prev.steps.map((s, si) => si === stepIndex ? {
                                  ...s,
                                  options: s.options.map((opt, oi) => oi === optIndex ? {
                                    ...opt, action, next_step
                                  } : opt)
                                } : s)
                              }));
                            }}
                            style={{ 
                              flex: 1, padding: '7px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px', cursor: 'pointer',
                              background: option.action === 'end' ? '#fef3c7' : option.action === 'reply_and_goto' ? '#dbeafe' : '#dcfce7'
                            }}
                          >
                            <option value="end">Reply & End Flow</option>
                            {formData.steps.map((s, si) => si !== stepIndex ? (
                              <option key={`goto_${s.id}`} value={`goto:${s.id}`}>
                                Go to Step {si + 1}
                              </option>
                            ) : null)}
                            {formData.steps.map((s, si) => si !== stepIndex ? (
                              <option key={`reply_goto_${s.id}`} value={`reply_goto:${s.id}`}>
                                Reply & Go to Step {si + 1}
                              </option>
                            ) : null)}
                          </select>
                          <button
                            onClick={() => removeOption(stepIndex, optIndex)}
                            style={{ padding: '6px', background: '#fee2e2', border: 'none', borderRadius: '4px', cursor: 'pointer', lineHeight: '1' }}
                          >
                            <X size={14} color="#dc2626" />
                          </button>
                        </div>
                        {(option.action === 'end' || option.action === 'reply_and_goto') && (
                          <input
                            value={option.response}
                            onChange={(e) => updateOption(stepIndex, optIndex, 'response', e.target.value)}
                            placeholder={option.action === 'end' ? "Response text (sent & flow ends)..." : "Reply message (sent before next step)..."}
                            style={{ width: '100%', padding: '7px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                          />
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => addOption(stepIndex)}
                      style={{ padding: '6px 12px', background: '#e0e7ff', color: '#4338ca', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      + Add Option
                    </button>
                  </div>
                ))}
                
                <button
                  onClick={addStep}
                  style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px dashed #94a3b8', borderRadius: '8px', width: '100%', cursor: 'pointer', color: '#64748b' }}
                >
                  + Add Step
                </button>
              </div>

              <button
                onClick={handleSubmit}
                disabled={saving}
                style={{
                  padding: '12px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginTop: '8px'
                }}
              >
                {saving ? 'Saving...' : (editingFlow ? 'Update Flow' : 'Create Flow')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// History Tab Component
function HistoryTab({ history, fetchHistory }) {
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <p style={{ color: '#64748b', fontSize: '14px' }}>
          Showing last {history.length} messages
        </p>
        <button
          onClick={fetchHistory}
          style={{
            padding: '8px 16px',
            background: '#f1f5f9',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Refresh
        </button>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {history.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            No chat history yet. Messages will appear here once the chatbot starts responding.
          </div>
        ) : (
          <div style={{ maxHeight: '500px', overflow: 'auto' }}>
            {history.map(msg => (
              <div
                key={msg.id}
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #f1f5f9',
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start'
                }}
              >
                <div style={{
                  padding: '4px 8px',
                  background: msg.direction === 'incoming' ? '#dbeafe' : '#dcfce7',
                  color: msg.direction === 'incoming' ? '#1e40af' : '#166534',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '500',
                  minWidth: '60px',
                  textAlign: 'center'
                }}>
                  {msg.direction === 'incoming' ? 'IN' : 'OUT'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                    {msg.phone_number} • {formatTime(msg.created_at)}
                  </div>
                  <div style={{ fontSize: '14px', color: '#1e293b' }}>
                    {msg.message_text}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
